const TIME_OF_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}[ T](\d{2}):(\d{2})(?::(\d{2}))?/;

// Seconds since midnight, ignoring calendar date. Trip set trips can be pinned
// from a different service date than the one the time window is anchored to
// (query.serviceDate), so comparing full date+time would wrongly exclude a
// trip set trip whose clock time is in range but whose date differs — the
// window is meant to filter by time-of-day only.
const toSecondsSinceMidnight = (value) => {
    if (!value) {
        return null;
    }

    const match = String(value).match(TIME_OF_DAY_PATTERN);
    if (!match) {
        return null;
    }

    const [, hours, minutes, seconds = '0'] = match;
    return (Number(hours) * 3600) + (Number(minutes) * 60) + Number(seconds);
};

/**
 * @param {string|null|undefined} timestamp
 * @param {string|null|undefined} windowStart
 * @param {string|null|undefined} windowEnd
 * @returns {boolean}
 */
export const isTimestampInWindow = (timestamp, windowStart, windowEnd) => {
    const value = toSecondsSinceMidnight(timestamp);
    if (value == null) {
        return false;
    }

    const start = toSecondsSinceMidnight(windowStart);
    const end = toSecondsSinceMidnight(windowEnd);

    if (start != null && value < start) {
        return false;
    }

    if (end != null && value > end) {
        return false;
    }

    return true;
};

/**
 * Combine the selected service date with a time-of-day (HH:mm, from a native
 * time input) into the "YYYY-MM-DD HH:mm:ss" shape trip timestamps use, so
 * isTimestampInWindow can compare it directly.
 *
 * @param {string|null|undefined} serviceDate - YYYY-MM-DD
 * @param {string|null|undefined} timeOfDay - HH:mm
 * @returns {string|null}
 */
export const buildTimeWindowTimestamp = (serviceDate, timeOfDay) => {
    if (!serviceDate || !timeOfDay) {
        return null;
    }

    return `${serviceDate} ${timeOfDay}:00`;
};

/**
 * Inverse of buildTimeWindowTimestamp — pulls the HH:mm back out for
 * populating a time input from stored query state.
 *
 * @param {string|null|undefined} timestamp - "YYYY-MM-DD HH:mm:ss"
 * @returns {string}
 */
export const extractTimeOfDay = (timestamp) => {
    if (!timestamp) {
        return '';
    }

    const match = String(timestamp).match(/^\d{4}-\d{2}-\d{2}[ T](\d{2}:\d{2})/);
    return match ? match[1] : '';
};

/**
 * Keep the same time-of-day bounds when the service date changes.
 *
 * @param {string|null|undefined} serviceDate
 * @param {string|null|undefined} windowStart
 * @param {string|null|undefined} windowEnd
 * @returns {{ start: string|null, end: string|null }}
 */
export const shiftTimeWindowToServiceDate = (serviceDate, windowStart, windowEnd) => {
    if (!serviceDate) {
        return { start: null, end: null };
    }

    return {
        start: buildTimeWindowTimestamp(serviceDate, extractTimeOfDay(windowStart)),
        end: buildTimeWindowTimestamp(serviceDate, extractTimeOfDay(windowEnd)),
    };
};
